import React, { useEffect } from 'react';
import { tx, tr } from '../../utils/i18n';
import { routePath } from '../../i18n/routes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, CreditCard, Users, TrendingUp, Shield, Target, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Header from '../Header';
import Footer from '../Footer';
import { Toaster } from 'sonner';
import SEOHead from '../SEOHead';

const BNPLService = () => {
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "BNPL Advertising - Buy Now Pay Later",
    "description": "Publicité sur plateformes de paiement fractionné : Klarna, Afterpay, ZIP. AOV élevés, audiences qualifiées.",
    "provider": {
      "@type": "Organization",
      "name": "OptimHub"
    }
  };

  const features = [
    {
      icon: CreditCard,
      title: 'Klarna, Afterpay, ZIP',
      description: tx(currentLanguage, 'Partenariats avec les leaders mondiaux du Buy Now Pay Later', 'Partnerships with global Buy Now Pay Later leaders', 'Partnerschaften mit globalen Buy Now Pay Later-Führern')
    },
    {
      icon: Users,
      title: tx(currentLanguage, 'AOV Élevés', 'High AOV', 'Hoher AOV'),
      description: tx(currentLanguage, 'Audiences avec panier moyen 3x supérieur grâce aux solutions de paiement', 'Audiences with 3x higher average basket thanks to payment solutions', 'Zielgruppen mit 3x höherem Durchschnittswarenkorb dank Zahlungslösungen')
    },
    {
      icon: Shield,
      title: tx(currentLanguage, 'Audiences Qualifiées', 'Qualified Audiences', 'Qualifizierte Zielgruppen'),
      description: tx(currentLanguage, 'Utilisateurs pré-qualifiés financièrement avec forte intention d\'achat', 'Financially pre-qualified users with strong purchase intent', 'Finanziell vorqualifizierte Nutzer mit starker Kaufabsicht')
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: tx(currentLanguage, 'Conversion Premium', 'Premium Conversion', 'Premium-Conversion'),
      description: tx(currentLanguage, 'Taux de conversion 4x supérieur sur les gros montants', '4x higher conversion rate on large amounts', '4x höhere Conversion-Rate bei großen Beträgen')
    },
    {
      icon: TrendingUp,
      title: tx(currentLanguage, 'Panier Moyen Élevé', 'High Average Basket', 'Hoher Durchschnittswarenkorb'),
      description: tx(currentLanguage, 'Augmentation du panier moyen de 35% en moyenne', '35% average increase in average basket', '35% durchschnittliche Erhöhung des Durchschnittswarenkorbs')
    },
    {
      icon: Zap,
      title: tx(currentLanguage, 'Financement Simplifié', 'Simplified Financing', 'Vereinfachte Finanzierung'),
      description: tx(currentLanguage, 'Suppression des barrières d\'achat pour vos produits premium', 'Removal of purchase barriers for your premium products', 'Beseitigung von Kaufbarrieren für Ihre Premium-Produkte')
    }
  ];

  const stats = [
    { value: '250%', label: tx(currentLanguage, 'Croissance BNPL', 'BNPL Growth', 'BNPL-Wachstum') },
    { value: '45M+', label: tx(currentLanguage, 'Utilisateurs Actifs', 'Active Users', 'Aktive Nutzer') },
    { value: '€180', label: tx(currentLanguage, 'Panier Moyen', 'Average Basket', 'Durchschnittswarenkorb') }
  ];

  const partners = [
    'Klarna', 'Afterpay', 'ZIP', 'PayPal Pay in 4', 'Sezzle', 'Affirm'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={tr(currentLanguage, "BNPL Advertising | Klarna, Afterpay, ZIP Ads - Buy Now Pay Later - OptimHub")}
        description={tr(currentLanguage, "Publicité sur plateformes BNPL : Klarna, Afterpay, ZIP. AOV 3x supérieur, 45M+ utilisateurs actifs. Content commerce pour audiences premium à haut pouvoir d'achat.")}
        keywords="BNPL advertising, buy now pay later ads, Klarna advertising, Afterpay ads, ZIP advertising, fintech advertising, payment platform ads, high AOV advertising, content commerce BNPL"
        ogImage="https://images.unsplash.com/photo-1648161235886-32b6764917e6?w=1200"
        routeKey="serviceBnpl"
        lang={currentLanguage}
        schemaData={schemaData}
      />
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
                {tx(currentLanguage, 'Buy Now Pay Later', 'Buy Now Pay Later', 'Jetzt kaufen, später zahlen')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                BNPL
                <span className="block text-blue-600 mt-2">
                  {tx(currentLanguage, 'Advertising', 'Advertising', 'Werbung')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {tx(currentLanguage, 'Connectez-vous avec des consommateurs motivés juste au moment où ils cherchent des options pour financer leurs achats désirés. Partenaires avec Klarna, Afterpay, ZIP et plus. En tant que traffic brokers, nous achetons du trafic au CPC et vous ne payez qu\'au CPA.', 'Connect with motivated shoppers just as they\'re looking for options to finance their desired purchases. Partners with Klarna, Afterpay, ZIP and more. As traffic brokers, we buy traffic at CPC and you only pay at CPA.', 'Verbinden Sie sich mit motivierten Käufern genau dann, wenn sie nach Optionen suchen, um ihre gewünschten Einkäufe zu finanzieren. Partner mit Klarna, Afterpay, ZIP und mehr. Als Traffic-Broker kaufen wir Traffic zu CPC und Sie zahlen nur zu CPA.')
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Commencer maintenant', 'Get Started', 'Jetzt starten')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a 
                  href="mailto:contact@optimhub.com?subject=Demande de démo BNPL"
                  className="border-2 border-blue-200 text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Voir la démo', 'Watch Demo', 'Demo ansehen')}
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1648161235886-32b6764917e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmaW50ZWNoJTIwcGF5bWVudHxlbnwwfHx8Ymx1ZXwxNzYxNTc3MzA2fDA&ixlib=rb-4.1.0&q=85"
                alt="BNPL Advertising"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            {tx(currentLanguage, 'Nos Partenaires BNPL', 'Our BNPL Partners', 'Unsere BNPL-Partner')}
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="bg-gray-100 px-6 py-3 rounded-lg font-semibold text-gray-700">
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Solutions BNPL Premium', 'Premium BNPL Solutions', 'Premium-BNPL-Lösungen')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Capturez des audiences avec un pouvoir d\'achat élevé au moment où elles recherchent des solutions de financement.', 'Capture audiences with high purchasing power when they\'re looking for financing solutions.', 'Erfassen Sie Zielgruppen mit hoher Kaufkraft, wenn sie nach Finanzierungslösungen suchen.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-center">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Pourquoi BNPL ?', 'Why BNPL?', 'Warum BNPL?')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {tx(currentLanguage, 'Prêt à Augmenter Vos Ventes ?', 'Ready to Increase Your Sales?', 'Bereit, Ihre Verkäufe zu steigern?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {tx(currentLanguage, 'Rejoignez des marques premium qui utilisent notre réseau BNPL pour augmenter leur panier moyen.', 'Join premium brands using our BNPL network to increase their average basket.', 'Schließen Sie sich Premium-Marken an, die unser BNPL-Netzwerk nutzen, um ihren Durchschnittswarenkorb zu erhöhen.')
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
            >
              {tx(currentLanguage, 'Démarrer maintenant', 'Start Now', 'Jetzt starten')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200"
            >
              {tx(currentLanguage, 'Parler à un expert', 'Talk to an Expert', 'Mit Experten sprechen')}
            </button>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

export default BNPLService;